import {
  Component,
  ElementRef,
  inject,
  signal,
  viewChild,
  viewChildren,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { PuebloBase } from '@components/pueblo-base.component';
import { environment } from '@env/environment';
import { AnimationController } from '@ionic/angular/standalone';
import { DEFAULT_COLOR } from '@utils/defaults';
import { interval } from 'rxjs';
import { UserAvatar } from '../user-avatar/user-avatar.component';

interface AnimationInterest {
  emoji: string;
  name: string;
  users: Set<number>;
}

interface AnimationInterestUser {
  avatarUrl: string;
  index: number;
}

const SOCCER_INTEREST = {
  emoji: '⚽',
  name: 'Soccer',
  users: new Set<number>([1, 2, 4, 5, 7]),
};

const WINE_INTEREST = {
  emoji: '🍷',
  name: 'unWINEd',
  users: new Set<number>([2, 3, 4, 6, 7]),
};

const INTEREST_DURATION_MS = 1200;

@Component({
  selector: 'intro-flow-welcome-view',
  templateUrl: 'welcome-view.component.html',
  styleUrl: 'welcome-view.component.scss',
  imports: [UserAvatar],
})
export class IntroFlowWelcomeView extends PuebloBase {
  private readonly animationCtrl = inject(AnimationController);

  protected readonly title =
    environment.env !== 'prod' ? `pueblo (${environment.env})` : 'pueblo';

  protected readonly DEFAULT_COLOR = DEFAULT_COLOR;

  private readonly $interestItem1 =
    viewChild.required<ElementRef<HTMLElement>>('interestItem1');
  private readonly $interestItem2 =
    viewChild.required<ElementRef<HTMLElement>>('interestItem2');
  private readonly $activeInterest = signal(1);
  private readonly $userItems =
    viewChildren<ElementRef<HTMLElement>>('userItem');

  protected readonly $interestItem1Value = signal(SOCCER_INTEREST);
  protected readonly $interestItem2Value = signal(WINE_INTEREST);

  protected readonly userItemsByIndex = new Map<
    number,
    ElementRef<HTMLElement>
  >();

  protected readonly interests: AnimationInterest[] = [
    SOCCER_INTEREST,
    WINE_INTEREST,
    {
      emoji: '💪',
      name: 'Gym',
      users: new Set<number>([1, 2, 4, 5, 6]),
    },
    {
      emoji: '🛍️',
      name: 'Groceries',
      users: new Set<number>([0, 1, 2, 3, 5, 6]),
    },
    {
      emoji: '💃',
      name: 'Clubbers',
      users: new Set<number>([0, 1, 2, 3, 4, 5, 6, 7]),
    },
  ];

  protected readonly users: AnimationInterestUser[] = [
    {
      avatarUrl:
        'http://localhost:9199/glossy-beach-335815.appspot.com/profilePics/alex.png',
      index: 0,
    },
    {
      avatarUrl:
        'http://localhost:9199/glossy-beach-335815.appspot.com/profilePics/angel.png',
      index: 1,
    },
    {
      avatarUrl:
        'http://localhost:9199/glossy-beach-335815.appspot.com/profilePics/wanda.png',
      index: 2,
    },
    {
      avatarUrl:
        'http://localhost:9199/glossy-beach-335815.appspot.com/profilePics/rose.png',
      index: 3,
    },
    {
      avatarUrl:
        'http://localhost:9199/glossy-beach-335815.appspot.com/profilePics/liam.png',
      index: 4,
    },
    {
      avatarUrl:
        'http://localhost:9199/glossy-beach-335815.appspot.com/profilePics/olivia.png',
      index: 5,
    },
    {
      avatarUrl:
        'http://localhost:9199/glossy-beach-335815.appspot.com/profilePics/noah.png',
      index: 6,
    },
    {
      avatarUrl:
        'http://localhost:9199/glossy-beach-335815.appspot.com/profilePics/emma.png',
      index: 7,
    },
  ];

  constructor() {
    super();

    interval(INTEREST_DURATION_MS)
      .pipe(takeUntilDestroyed())
      .subscribe(() => {
        this.runChangeAnimation();
      });
  }

  ngAfterViewInit(): void {
    for (const user of this.$userItems()) {
      const index = Number(user.nativeElement.dataset.index);
      this.userItemsByIndex.set(index, user);
      if (SOCCER_INTEREST.users.has(index)) {
        user.nativeElement.style.opacity = '1';
      } else {
        user.nativeElement.style.opacity = '0';
      }
    }
    this.$interestItem1().nativeElement.style.opacity = '1';
    this.$interestItem2().nativeElement.style.opacity = '0';
  }

  protected runChangeAnimation() {
    const currInterestElem =
      this.$activeInterest() === 1
        ? this.$interestItem1()
        : this.$interestItem2();
    const nextInterestElem =
      this.$activeInterest() === 1
        ? this.$interestItem2()
        : this.$interestItem1();

    const currInterestValue =
      this.$activeInterest() === 1
        ? this.$interestItem1Value()
        : this.$interestItem2Value();

    const currInterestIndex = this.interests.findIndex(
      (interest) => interest.emoji === currInterestValue.emoji,
    );
    const nextInterestValue =
      this.interests[(currInterestIndex + 1) % this.interests.length];
    if (this.$activeInterest() === 1) {
      this.$interestItem2Value.set(nextInterestValue);
    } else {
      this.$interestItem1Value.set(nextInterestValue);
    }

    const toEnter: HTMLElement[] = [];
    const toExit: HTMLElement[] = [];
    for (const user of this.users) {
      const userElem = this.userItemsByIndex.get(user.index);
      if (!userElem) {
        throw new Error('User element not found for index: ' + user.index);
      }
      if (
        !currInterestValue.users.has(user.index) &&
        nextInterestValue.users.has(user.index)
      ) {
        toEnter.push(userElem.nativeElement);
      } else if (
        currInterestValue.users.has(user.index) &&
        !nextInterestValue.users.has(user.index)
      ) {
        toExit.push(userElem.nativeElement);
      }
    }

    for (const user of toEnter) {
      const avatar = user.querySelector('.user-avatar') as HTMLElement;
      if (!avatar) {
        throw new Error('Avatar or line not found in user element');
      }
      const scaleAnimation = this.animationCtrl
        .create()
        .addElement(avatar)
        .duration(300)
        .delay(300)
        .keyframes([
          { offset: 0, scale: 0.5 },
          { offset: 0.5, scale: 1.2 },
          { offset: 1, scale: 1 },
        ])
        .onFinish(() => {
          scaleAnimation.stop();
          scaleAnimation.destroy();
        });
      scaleAnimation.play();
      const opacityAnimation = this.animationCtrl
        .create()
        .addElement(user)
        .duration(300)
        .delay(300)
        .keyframes([
          { offset: 0, opacity: 0 },
          { offset: 1, opacity: 1 },
        ])
        .onFinish(() => {
          user.style.opacity = '1';
          opacityAnimation.stop();
          opacityAnimation.destroy();
        });
      opacityAnimation.play();
    }

    for (const user of toExit) {
      const avatar = user.querySelector('.user-avatar') as HTMLElement;
      if (!avatar) {
        throw new Error('Avatar or line not found in user element');
      }
      const opacityAnimation = this.animationCtrl
        .create()
        .addElement(user)
        .duration(200)
        .delay(350)
        .keyframes([
          { offset: 0, opacity: 1 },
          { offset: 1, opacity: 0 },
        ])
        .onFinish(() => {
          user.style.opacity = '0';
          opacityAnimation.stop();
          opacityAnimation.destroy();
        });
      opacityAnimation.play();
    }

    const interestOutAnimation = this.animationCtrl
      .create()
      .addElement(currInterestElem.nativeElement)
      .duration(500)
      .easing('ease-in')
      .keyframes([
        { offset: 0, opacity: 1, transform: 'rotate(0)' },
        { offset: 0.5, opacity: 1, transform: 'rotate(-4deg)' },
        { offset: 1, opacity: 0, transform: 'rotate(90deg)' },
      ])
      .onFinish(() => {
        interestOutAnimation.stop();
        interestOutAnimation.destroy();
        currInterestElem.nativeElement.style.opacity = '0';
        currInterestElem.nativeElement.style.transform = 'rotate(0)';
      });
    interestOutAnimation.play();

    const interestInAnimation = this.animationCtrl
      .create()
      .addElement(nextInterestElem.nativeElement)
      .duration(500)
      .delay(100)
      .easing('ease-out')
      .keyframes([
        { offset: 0.7, opacity: 0, transform: 'rotate(-45deg)' },
        { offset: 1, opacity: 1, transform: 'rotate(0deg)' },
      ])
      .onFinish(() => {
        interestInAnimation.stop();
        interestInAnimation.destroy();
        nextInterestElem.nativeElement.style.opacity = '1';
        nextInterestElem.nativeElement.style.transform = 'rotate(0)';
      });
    interestInAnimation.play();

    this.$activeInterest.set(this.$activeInterest() === 1 ? 2 : 1);
  }
}
